/* eslint-disable react/no-array-index-key */
import React, {useEffect} from 'react';
import {useNavigate} from "react-router-dom";
import useIsMobile from "../../hooks/useIsMobile";
import {Col, Row} from "antd";
import {
  StepItemContent, StepItemWrapper,
  StepProgressLine,
  StepsContainer,
  StepsWrapper, Tag, TagsWrapper
} from "../AvailableFlights/styles";
import {StyledParagraph, StyledTitle} from "../CustomAntdComponents/Typography";
import { ReactComponent as BusIcon } from '../../assets/images/bus-grey.svg';
import {formatNumberAsCurrency} from "../../utils/helpers";
import {useRoadSearchStore} from "../../store/roadSearch/roadSearchStore";
import {formatSelectedSeats, getBusTypeImage, getRoadTransportLogo} from "../AvailableBuses/helper";
import {BusCardContent, BusCardImage, BusImage, BusStepWrapper} from "../AvailableBuses/styles";
import {ReactComponent as PassengerIcon} from "../../assets/images/passenger.svg";
import {ReactComponent as ClockIcon} from "../../assets/images/clock.svg";
import styled from "styled-components";
import moment from "moment";

type Props = {
  receipt?: boolean;
}

function RoadItinerarySummary({ receipt }: Props) {
  const { isMobile } = useIsMobile();
  const { selectedBus, selectedBusSeat, selectedResult } = useRoadSearchStore();

  const navigate = useNavigate();

  useEffect(() => {
    if (!selectedBus) {
      navigate('/search/road');
    }
  }, [selectedBus, navigate]);

  const departureBus = selectedBus[0];
  const returningBus = selectedBus && selectedBus.length == 2 ? selectedBus[1] : null;
  const departureOperator = selectedResult[0];
  const returningOperator = selectedResult[1];
  const departureSeats = selectedBusSeat[0];
  const returningSeats = selectedBusSeat[1];

  const renderMobileTripRow = (operator: BusResultItem, bus: AvailableBus, seats: SelectedBusSeat) => {
    return (
       <div>
         <Row>
           <Col span={receipt ? 0 : 6}>
             <BusCardImage>
               <img src={bus.Operator?.Logo || getRoadTransportLogo(operator.AgentName)} alt={bus.Operator?.Name || operator.AgentName} width={50} />
               <BusImage src={getBusTypeImage(bus.Capacity)} alt="bus" />
             </BusCardImage>
           </Col>

           <Col span={receipt ? 24 : 18}>
             <BusCardContent style={ receipt ? { padding: 0, border: 'none' } : {}}>
               {
                    !receipt && (
                       <StyledTitle level={5}>
                         {bus.BusType}
                       </StyledTitle>
                   )
               }

               <BusStepWrapper>
                 <div>
                   <StyledTitle level={5}>
                     {operator.DepartureTerminal}
                   </StyledTitle>
                   <StyledParagraph size="default">
                     {
                         bus.BoardingDetails && `${bus.BoardingDetails.Name}`
                     }
                   </StyledParagraph>
                 </div>

                 <StepsContainer>
                   <StepsWrapper>
                     <StepProgressLine />
                     <StepItemWrapper>
                       <StepItemContent />
                     </StepItemWrapper>

                     <StepProgressLine />
                   </StepsWrapper>
                 </StepsContainer>

                 <div>
                   <StyledTitle level={5} align="left">
                     {operator.ArrivalTerminal}
                   </StyledTitle>
                   <StyledParagraph size="default">
                     {
                         bus.DropOffDetails && `${bus.DropOffDetails.Name}`
                     }
                   </StyledParagraph>
                 </div>
               </BusStepWrapper>

               <TagsWrapper>
                 <Tag>
                   <ClockIcon />
                   {moment(bus.EstimatedDepartureDate).format('DD MMM, hh:mm A')}
                 </Tag>
                 <Tag>
                   <PassengerIcon />
                   {`Seats selected: ${formatSelectedSeats(seats)}`}
                 </Tag>
               </TagsWrapper>
             </BusCardContent>
           </Col>
         </Row>

         {
           !receipt && (
                 <Row>
                   <Col span={24}>
                     <StyledTitle level={4} align="right" style={{ padding: "12px" }}>
                       {formatNumberAsCurrency(bus.TotalFare, operator.Currency)}
                     </StyledTitle>
                   </Col>
                 </Row>
             )
         }
       </div>
    )
  }

  const renderTripRow = (operator: BusResultItem, bus: AvailableBus, seats: SelectedBusSeat) => {
    const formatItineraryTime = (dateStr: string, fallbackTime: string) => {
      if (!dateStr) return fallbackTime;
      const m = moment(dateStr);
      return m.isValid() ? m.format("hh:mm a") : fallbackTime;
    };
    const depTime = formatItineraryTime(bus.EstimatedDepartureDate, bus.EstimatedDepartureTime);
    const arrTime = formatItineraryTime(bus.EstimatedArrivalDate, bus.EstimatedArrivalTime);
    const formattedDuration = bus.Duration ? bus.Duration.replace(':', ' ') : '';
    const formattedDate = moment(bus.EstimatedDepartureDate).isValid() 
      ? moment(bus.EstimatedDepartureDate).format('ddd DD, MMM  h:mm a')
      : '';

    return (
        <> 
        <Row>
          <Col span={receipt ? 0 : 8}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', height: '100%', paddingLeft: '15px' }}>
              <img src={bus.Operator?.Logo || getRoadTransportLogo(operator.AgentName)} alt={bus.Operator?.Name || operator.AgentName} width={40} height={40} style={{ borderRadius: '50%', objectFit: 'contain' }} />
              <StyledTitle level={5} style={{ margin: 0, color: '#15162B', fontWeight: 590 }}>
                {bus.Operator?.Name || operator.AgentName}
              </StyledTitle>
            </div>
          </Col>
          <Col span={receipt ? 24 : 16}>
            <BusCardContent receipt={Boolean(receipt)}>
              <BusStepWrapper style={{ alignItems: 'center' }}>
                <div>
                  <StyledTitle level={3} style={{ margin: 0, fontWeight: 590, color: '#15162B', fontSize: '20px', lineHeight: '22px', whiteSpace: 'nowrap' }}>
                    {depTime}
                  </StyledTitle>
                  <StyledParagraph style={{ color: '#72737F', fontSize: '13px', margin: '4px 0 0 0', lineHeight: '16px' }}>
                    {operator.DepartureTerminal}
                  </StyledParagraph>
                </div>

                <ItineraryLine>
                  <div className="line" />
                  <div className="dot start" />
                  <div className="duration">{formattedDuration}</div>
                  <div className="dot end" />
                </ItineraryLine>

                <div>
                  <StyledTitle level={3} style={{ margin: 0, fontWeight: 590, color: '#15162B', fontSize: '20px', lineHeight: '22px', whiteSpace: 'nowrap' }} align="left">
                    {arrTime}
                  </StyledTitle>
                  <StyledParagraph style={{ color: '#72737F', fontSize: '13px', margin: '4px 0 0 0', lineHeight: '16px' }} align="left">
                    {operator.ArrivalTerminal}
                  </StyledParagraph>
                </div>
              </BusStepWrapper>

              {/* <TagsWrapper receipt={receipt}>
                <Tag>
                  <ClockIcon />
                  {formattedDate}
                </Tag>
                <Tag>
                  <PassengerIcon />
                  {`Seats selected: ${formatSelectedSeats(seats)}`}
                </Tag>
              </TagsWrapper> */}
            </BusCardContent>
          </Col>
        </Row>
        <Row>
          
          <TagsWrapper receipt={receipt}>
                <Tag>
                  <ClockIcon />
                  {formattedDate}
                </Tag>
                <Tag>
                  <PassengerIcon />
                  {`Seats selected: ${formatSelectedSeats(seats)}`}
                </Tag>
              </TagsWrapper>
          
        </Row>
        </>
    )
  }

  return isMobile ? (
      <Wrapper>
        {renderMobileTripRow(departureOperator, departureBus, departureSeats)}

        {
            returningBus && returningOperator && renderMobileTripRow(returningOperator, returningBus, returningSeats)
        }
      </Wrapper>
  ) : (
      <Wrapper>
        {renderTripRow(departureOperator, departureBus, departureSeats)}

        {
            returningBus && returningOperator && renderTripRow(returningOperator, returningBus, returningSeats)
        }
      </Wrapper>
  )
}
export default RoadItinerarySummary;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
`;

const ItineraryLine = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin: 0 16px;
  height: 20px;
  min-width: 60px;
  
  .line {
    height: 1px;
    background: #E5E7EB;
    width: 100%;
    position: absolute;
    top: 50%;
    left: 0;
    z-index: 1;
  }
  
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ffffff;
    border: 1px solid #9CA3AF;
    position: absolute;
    top: 50%;
    margin-top: -4px;
    z-index: 2;
    
    &.start {
      left: 0;
    }
    
    &.end {
      right: 0;
    }
  }
  
  .duration {
    background: #ffffff;
    padding: 0 12px;
    font-size: 12px;
    color: #6B7280;
    z-index: 3;
    font-weight: 500;
  }
`;
